import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Form, Button, Spinner, Container, Table, Modal, Toast, Alert, Card, InputGroup, Collapse, Badge } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp, FaSort, FaSortUp, FaSortDown, FaSearch } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  DoughnutController,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  getCirclesByOrgId,
  getGradingSystemsByOrgId,
  generateReport,
  getReportsByOrganization,
  getReportById,
  getReportStatus,
  deleteReport,
  exportReportToPDF,
  exportReportToExcel,
  ORG_ID_CHANGE_EVENT,
  getQuestionnairesByOrganization,
  getQuestionnaires,
  getReportsUsageSummary,
  getOrganizationNames,
  getUserRoles,
} from '../services/api';
import ReportSections from './ReportSections';

// Register ChartJS components and plugins
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  DoughnutController,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const REMCHANNEL_COLORS = {
  steelBlue: '#003366',
  green: '#00A651',
  black: '#000000',
  paper: '#FFFFFF',
  orange: '#FF6B00',
  rust: '#C35214',
  blue: '#1B3664',
  grey: '#666666',
  // Tints/Shades
  steelBlue80: '#33587A',
  steelBlue60: '#668BAF',
  steelBlue40: '#99BFD4',
  steelBlue20: '#CCDFF0',
  green80: '#33B86B',
  green60: '#66CB8C',
  green40: '#99DEAD',
  green20: '#CCF1CE',
  orange80: '#FF8A33',
  orange60: '#FFA866',
  orange40: '#FFC699',
  orange20: '#FFE4CC',
  rust80: '#D16B33',
  rust60: '#E18566',
  rust40: '#F1A099',
  rust20: '#F9CABB',
  blue80: '#495A7A',
  blue60: '#7A8BAF',
  blue40: '#AABFD4',
  blue20: '#D5DFF0',
  grey80: '#888888',
  grey60: '#AAAAAA',
  grey40: '#CCCCCC',
  grey20: '#EEEEEE',
};

// Theme colors
const THEME = {
  // Primary greens
  primary: '#00A651', // Main green
  primaryDark: '#008c44', // Darker green for hover states
  primaryDarker: '#006633', // Even darker green for active states
  primaryLight: '#e8f5e9', // Light green for backgrounds
  primaryLighter: '#f1f8e9', // Even lighter green for subtle backgrounds
  primaryBorder: '#4caf50', // Green for borders
  
  // Grey tones
  grey900: '#212529', // Darkest grey for primary text
  grey800: '#343a40', // Dark grey for secondary text
  grey700: '#495057', // Grey for headings
  grey600: '#6c757d', // Grey for secondary text
  grey500: '#adb5bd', // Grey for borders
  grey400: '#ced4da', // Light grey for disabled states
  grey300: '#dee2e6', // Lighter grey for borders
  grey200: '#e9ecef', // Very light grey for backgrounds
  grey100: '#f8f9fa', // Lightest grey for backgrounds
  
  // Status colors
  success: '#00A651', // Success green
  warning: '#ffc107', // Warning yellow
  danger: '#dc3545', // Danger red
  info: '#17a2b8', // Info blue
  
  // Base colors
  background: '#ffffff',
  backgroundAlt: '#f8f9fa',
  border: '#dee2e6',
  shadow: 'rgba(0, 0, 0, 0.1)'
};

// Add global styles
const GLOBAL_STYLES = {
  card: {
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${THEME.grey300}`,
    borderRadius: '8px'
  },
  button: {
    primary: {
      backgroundColor: THEME.primary,
      borderColor: THEME.primary,
      color: '#fff',
      '&:hover': {
        backgroundColor: THEME.primaryDark,
        borderColor: THEME.primaryDark
      }
    },
    secondary: {
      backgroundColor: THEME.grey200,
      borderColor: THEME.grey300,
      color: THEME.grey800,
      '&:hover': {
        backgroundColor: THEME.grey300,
        borderColor: THEME.grey400
      }
    }
  },
  input: {
    border: `1px solid ${THEME.grey300}`,
    borderRadius: '4px',
    '&:focus': {
      borderColor: THEME.primary,
      boxShadow: `0 0 0 0.2rem ${THEME.primaryLight}`
    }
  }
};


// Toast component for notifications
const ToastMessage = ({ show, variant, message, onClose }) => {
  return (
    <Toast 
      onClose={onClose} 
      show={show} 
      delay={5000} 
      autohide
      className="position-fixed"
      style={{ 
        top: 20, 
        right: 20, 
        minWidth: '250px',
        zIndex: 9999
      }}
      bg={variant}
    >
      <Toast.Header>
        <strong className="me-auto">
          {variant === 'success' ? 'Success' : variant === 'danger' ? 'Error' : 'Notification'}
        </strong>
      </Toast.Header>
      <Toast.Body className={variant === 'danger' || variant === 'success' ? 'text-white' : ''}>
        {message}
      </Toast.Body>
    </Toast>
  );
};

// Reusable DataTable Component with sorting, filtering, and pagination
const DataTable = ({ 
  title, 
  data, 
  columns, 
  defaultOpen = true,
  searchable = true,
  pageSize = 10,
  collapsible = true
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(row => 
      columns.some(col => {
        const value = col.accessor ? col.accessor(row) : row[col.key];
        return value?.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [data, searchTerm, columns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = columns.find(c => c.key === sortConfig.key)?.accessor 
        ? columns.find(c => c.key === sortConfig.key).accessor(a)
        : a[sortConfig.key];
      const bValue = columns.find(c => c.key === sortConfig.key)?.accessor
        ? columns.find(c => c.key === sortConfig.key).accessor(b)
        : b[sortConfig.key];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const aStr = aValue.toString().toLowerCase();
      const bStr = bValue.toString().toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig, columns]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ms-1" style={{ opacity: 0.3 }} />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ms-1" /> 
      : <FaSortDown className="ms-1" />;
  };

  const tableContent = (
    <Card.Body>
      {searchable && (
        <InputGroup className="mb-3">
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </InputGroup>
      )}
      
      <div className="table-responsive">
        <Table hover striped bordered>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    fontSize: '12px'
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  {col.header}
                  {getSortIcon(col.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, index) => (
                <tr key={row.id || row.reportId || index}>
                  {columns.map(col => (
                    <td 
                      key={col.key}
                      style={{ fontSize: '12px' }}
                    >
                      {col.render 
                        ? col.render(row[col.key], row)
                        : (col.accessor ? col.accessor(row) : row[col.key] ?? '-')
                      }
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td 
                  colSpan={columns.length} 
                  className="text-center"
                  style={{ fontSize: '12px' }}
                >
                  {searchTerm ? 'No results found' : 'No data available'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} entries
          </div>
          <div>
            <Button
              variant="outline-success"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              className="me-2"
            >
              Previous
            </Button>
            <span className="mx-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline-success"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Card.Body>
  );

  if (!collapsible) {
    return (
      <Card className="mb-4">
        {tableContent}
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <Card.Header 
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(!open)}
      >
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{title}</h5>
          <span>
            {open ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </div>
      </Card.Header>
      <Collapse in={open}>
        <div>
          {tableContent}
        </div>
      </Collapse>
    </Card>
  );
};

const Reporting = ({ orgId }) => {
  const [circles, setCircles] = useState([]);
  const [gradingSystems, setGradingSystems] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState('');
  const [selectedGradingSystem, setSelectedGradingSystem] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCircles, setLoadingCircles] = useState(false);
  const [loadingGradingSystems, setLoadingGradingSystems] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('create');
  const [reportName, setReportName] = useState('');
  const [description, setDescription] = useState('');
  const [dataAugmentedPayMix, setDataAugmentedPayMix] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [hasActiveQuestionnaire, setHasActiveQuestionnaire] = useState(false);
  
  // For report view
  const [selectedJob, setSelectedJob] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  
  // For export
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingUsageStats, setExportingUsageStats] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfTotalPages, setPdfTotalPages] = useState(1);
  const pdfPageSyncRef = useRef(null);
  
  // KAM role checking for PDF export
  const [hasAllowedRole, setHasAllowedRole] = useState(false);
  const isEmbedded = window.self !== window.top;
  
  // Toast notification states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [step, setStep] = useState(1);

  // For default report name
  const [reportSequence, setReportSequence] = useState(1);

  useEffect(() => {
    loadData(orgId);

    // Add event listener for organization ID changes
    window.addEventListener(ORG_ID_CHANGE_EVENT, handleOrgIdChange);

    return () => {
      window.removeEventListener(ORG_ID_CHANGE_EVENT, handleOrgIdChange);
    };
  }, [orgId]);

  // Check for at least one ACTIVE questionnaire
  useEffect(() => {
    const checkActiveQuestionnaires = async () => {
      try {
        const questionnaires = await getQuestionnairesByOrganization(orgId);
        const hasActive = questionnaires.some(q => q.status === 'ACTIVE');
        setHasActiveQuestionnaire(hasActive);
      } catch (err) {
        console.error('Error checking active questionnaires:', err);
        setHasActiveQuestionnaire(false);
      }
    };
    checkActiveQuestionnaires();
  }, [orgId]);

  // Check user roles for KAM access (for PDF export)
  useEffect(() => {
    const checkUserRoles = async () => {
      if (orgId) {
        try {
          const roles = await getUserRoles();
          // Only apply role restrictions in embedded mode
          if (isEmbedded) {
            const allowedRoles = [1, 2, 3, 4, 5]; // KAM roles
            const hasRole = roles.some((role) =>
              allowedRoles.includes(role.RoleID)
            );
            setHasAllowedRole(hasRole);
          } else {
            // In non-embedded mode, always allow access
            setHasAllowedRole(true);
          }
        } catch (error) {
          console.error('Error checking user roles:', error);
          // Default to false on error in embedded mode
          setHasAllowedRole(!isEmbedded);
        }
      }
    };
    checkUserRoles();
  }, [orgId, isEmbedded]);

  // Generate default report name
  useEffect(() => {
    if (jobs && jobs.length) {
      setReportSequence(jobs.length + 1);
    }
  }, [jobs]);

  // Show toast notification
  const showNotification = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  // Hide toast notification
  const hideNotification = () => {
    setShowToast(false);
  };

  const handleOrgIdChange = (event) => {
    // Handle new event structure which is now an object
    const detail = event.detail;
    const newOrgId = typeof detail === 'object' ? (detail.orgId || '1066') : detail || '1066';
    const source = typeof detail === 'object' ? detail.source : null;
    
    // Log the change with source if available
    if (source === 'manual_refresh') {
      // console.log(`Reporting: Refreshing data for organization ID: ${newOrgId}`);
    } else if (typeof detail === 'object' && detail.previousOrgId) {
      // console.log(`Reporting: Organization changed from ${detail.previousOrgId || 'unknown'} to ${newOrgId}`);
    }
    
    // Reset state variables
    setCircles([]);
    setGradingSystems([]);
    setSelectedCircle('');
    setSelectedGradingSystem('');
    setReportName('');
    setDescription('');
    setError('');
    setSuccess('');
    setSelectedJob(null);
    setReportData(null);
    
    // Show notification for org ID change
    showNotification(`Organization ID changed to ${newOrgId}`, 'info');
    
    // Reload data with a slight delay to allow the event to fully process
    setTimeout(() => {
      loadData(newOrgId);
    }, 100);
  };

  const loadData = async (targetOrgId) => {
    setLoading(true);
    setError('');
    
    // Load jobs first
    try {
      const reportsResponse = await getReportsByOrganization(targetOrgId || orgId);

      
      if (reportsResponse) {
        setJobs(reportsResponse.map(report => ({
          reportId: report.id,
          title: report.name,
          description: report.description,
          status: report.status,
          circleId: report.circleId,
          circleName: circles.find(c => c.id === report.circleId)?.name || report.circleId,
          gradingSystemId: report.gradingSystemId,
          gradingSystemName: gradingSystems.find(s => s.id === report.gradingSystemId)?.name || report.gradingSystemId,
          createdAt: report.createdAt,
          completedAt: report.completedAt,
          error: report.error
          // The list omits reportData and translatedReportData; open the report by id for the full payload.
        })));
      } else {
        setJobs([]);
      }

    } catch (err) {
      console.error('Error loading reports:', err);
      showNotification('Failed to load reports. Please try again later.', 'danger');
    }
    
    // Now load circles in parallel with grading systems
    await Promise.all([
      loadCircles(targetOrgId || orgId),
      loadGradingSystems(targetOrgId || orgId)
    ]);
    
    setLoading(false);
  };

  const loadCircles = async (targetOrgId) => {
    setLoadingCircles(true);
    try {
      // console.log(`Loading circles for org ID: ${targetOrgId}`);
      const circlesResponse = await getCirclesByOrgId(targetOrgId);
      
      // console.log('Circles response:', circlesResponse);
      
      if (circlesResponse?.result?.length > 0) {
        setCircles(circlesResponse.result.map(circle => ({
          id: circle.id,
          name: circle.circleName || circle.name || `Circle ${circle.id}`
        })));
      } else {
        console.warn('No circles found or invalid response', circlesResponse);
        setCircles([]);
        if (!circlesResponse?.successful) {
          showNotification('Failed to load circles. Please check your organization ID.', 'warning');
        }
      }
    } catch (err) {
      console.error('Error loading circles:', err);
      setCircles([]);
      showNotification('Error loading circles', 'danger');
    } finally {
      setLoadingCircles(false);
    }
  };

  const loadGradingSystems = async (targetOrgId) => {
    setLoadingGradingSystems(true);
    try {
      const gradingSystemsResponse = await getGradingSystemsByOrgId(targetOrgId);
            
      if (gradingSystemsResponse?.result?.length > 0) {
        setGradingSystems(gradingSystemsResponse.result.map(system => ({
          id: system.id,
          name: system.gradingSystemName || system.name || `Grading System ${system.id}`
        })));
      } else {
        console.warn('No grading systems found or invalid response', gradingSystemsResponse);
        setGradingSystems([]);
        if (!gradingSystemsResponse?.successful) {
          showNotification('Failed to load grading systems. Please check your organization ID.', 'warning');
        }
      }
    } catch (err) {
      console.error('Error loading grading systems:', err);
      setGradingSystems([]);
      showNotification('Error loading grading systems', 'danger');
    } finally {
      setLoadingGradingSystems(false);
    }
  };


  const handleViewReport = async (job) => {
    setSelectedJob(job);
    setLoadingReport(true);
    setReportError('');
    
    try {
      const statusResponse = await getReportStatus(job.reportId);

      if (statusResponse?.progress !== 100 && statusResponse?.status !== 'completed') {
        showNotification(`Report is not ready yet. Current status: ${statusResponse?.status.status || 'unknown'}`, 'warning');
        setReportError(`Report is not ready yet. Current status: ${statusResponse?.status.status || 'unknown'}`);
        setLoadingReport(false);
        return;
      }
      
      const resultResponse = await getReportById(job.reportId);
      
      if (resultResponse?.reportData) {
        const dataToTransform = resultResponse.reportData;
        

        setReportData({
          ...dataToTransform,
          isTranslated: resultResponse.isTranslated ?? !!resultResponse.translatedReportData
        });
        setShowReportModal(true);
      } else {
        showNotification('Failed to load report data. Please try again.', 'danger');
        setReportError('Failed to load report data. Please try again.');
      }
    } catch (err) {
      console.error('Error loading report:', err);
      showNotification(err.message || 'An error occurred while loading the report', 'danger');
      setReportError(err.message || 'An error occurred while loading the report');
    } finally {
      setLoadingReport(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get status badge variant
  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'processing':
      case 'queued':
        return 'warning';
      case 'failed':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Open modal and reset state
  const handleOpenCreateModal = () => {
    setReportName(`Remuneration Report #${reportSequence}`);
    setDescription('');
    setSelectedCircle('');
    setSelectedGradingSystem('');
    setDataAugmentedPayMix(true);
    setShowCreateModal(true);
  };

  // Modal content
  const renderCreateReportModal = () => (
    <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create New Report</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Report Name</Form.Label>
            <Form.Control
              type="text"
              value={reportName}
              onChange={e => setReportName(e.target.value)}
              placeholder="Enter a descriptive name"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional: Add details about this report"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Select Circle*</Form.Label>
            <Form.Select
              value={selectedCircle}
              onChange={e => setSelectedCircle(e.target.value)}
              disabled={loadingCircles}
            >
              <option value="">-- Select Circle --</option>
              {circles.map(circle => (
                <option key={circle.id} value={circle.id}>{circle.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Select Grading System*</Form.Label>
            <Form.Select
              value={selectedGradingSystem}
              onChange={e => setSelectedGradingSystem(e.target.value)}
              disabled={loadingGradingSystems}
            >
              <option value="">-- Select Grading System --</option>
              {gradingSystems.map(system => (
                <option key={system.id} value={system.id}>{system.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
         style={GLOBAL_STYLES.button.primary}
          onClick={async () => {
            setAnalyzing(true);
            try {
              const response = await generateReport({
                name: reportName,
                description: description,
                circleId: selectedCircle,
                gradingSystemId: selectedGradingSystem,
                organizationId: orgId,
                dataAugmentedPayMix: dataAugmentedPayMix
              });

              if (response?.success && response?.reportId) {
                showNotification(`Report generation started! Report ID: ${response.reportId}`, 'success');
                setShowCreateModal(false);
                setReportName('');
                setDescription('');
                setSelectedCircle('');
                setSelectedGradingSystem('');
                setDataAugmentedPayMix(true);
                // Refresh jobs

                const reportsResponse = await getReportsByOrganization(orgId);
                if (reportsResponse) {
                  setJobs(reportsResponse.map(report => ({
                    reportId: report.id,
                    title: report.name,
                    description: report.description,
                    status: report.status,
                    circleId: report.circleId,
                    circleName: report.circleName,
                    gradingSystemId: report.gradingSystemId,
                    gradingSystemName: report.gradingSystemName,
                    createdAt: report.createdAt,
                    completedAt: report.completedAt,
                    error: report.error
                  })));
                }
              } else {
                showNotification('Failed to create report.', 'danger');
              }
            } catch (err) {
              showNotification(err.message || 'Error creating report', 'danger');
            } finally {
              setAnalyzing(false);
            }
          }}
          disabled={!selectedCircle || !selectedGradingSystem || analyzing}
        >
          {analyzing ? 'Creating...' : 'Create Report'}
        </Button>
        <Button
          style={{
            backgroundColor: REMCHANNEL_COLORS.steelBlue,
            borderColor: REMCHANNEL_COLORS.steelBlue,
            color: '#fff'
          }}
          onClick={() => setShowCreateModal(false)}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // Effect to sync PDF page state with iframe navigation
  useEffect(() => {
    if (!showPdfPreview || !pdfPreviewUrl) return;

    const syncPdfPage = () => {
      try {
        const iframe = document.getElementById('pdf-preview-iframe');
        if (!iframe) return;

        // Check iframe src for page hash
        const srcHashMatch = iframe.src.match(/#page=(\d+)/);
        if (srcHashMatch) {
          const currentPage = parseInt(srcHashMatch[1]);
          if (currentPage && currentPage !== pdfPage && currentPage >= 1) {
            setPdfPage(currentPage);
          }
        }
      } catch (error) {
        // Silently handle errors
        console.debug('Could not sync PDF page:', error);
      }
    };

    // Poll for changes every 300ms to catch browser PDF viewer navigation
    // The browser PDF viewer may update the hash when navigating
    const intervalId = setInterval(syncPdfPage, 300);

    // Also sync when iframe loads
    const iframe = document.getElementById('pdf-preview-iframe');
    if (iframe) {
      const handleLoad = () => {
        syncPdfPage();
      };
      iframe.addEventListener('load', handleLoad);
      
      return () => {
        clearInterval(intervalId);
        iframe.removeEventListener('load', handleLoad);
      };
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [showPdfPreview, pdfPreviewUrl, pdfPage]);

  const renderPdfPreviewModal = () => {
    // Update PDF URL with page number for navigation
    const getPdfUrlWithPage = (url, page) => {
      if (!url) return url;
      // Remove existing page hash if any
      const baseUrl = url.split('#')[0];
      return `${baseUrl}#page=${page}`;
    };

    const updatePdfPage = (newPage) => {
      if (newPage < 1 || newPage > pdfTotalPages) return;
      
      setPdfPage(newPage);
      const newUrl = getPdfUrlWithPage(pdfPreviewUrl, newPage);
      
      // Update iframe src to navigate to the page
      const iframe = document.getElementById('pdf-preview-iframe');
      if (iframe) {
        // Force reload by updating src
        iframe.src = newUrl;
      }
    };

    const handlePreviousPage = () => {
      if (pdfPage > 1) {
        updatePdfPage(pdfPage - 1);
      }
    };

    const handleNextPage = () => {
      if (pdfPage < pdfTotalPages) {
        updatePdfPage(pdfPage + 1);
      }
    };

    const handlePageInput = (e) => {
      const page = parseInt(e.target.value);
      if (!isNaN(page) && page >= 1 && page <= pdfTotalPages) {
        updatePdfPage(page);
      }
    };

    const handlePageInputBlur = (e) => {
      // If input is invalid, reset to current page
      const page = parseInt(e.target.value);
      if (isNaN(page) || page < 1 || page > pdfTotalPages) {
        e.target.value = pdfPage;
      }
    };

    return (
      <Modal 
        show={showPdfPreview} 
        onHide={() => {
          setShowPdfPreview(false);
          setPdfPage(1);
          setPdfTotalPages(1);
          if (pdfPreviewUrl) {
            window.URL.revokeObjectURL(pdfPreviewUrl);
            setPdfPreviewUrl(null);
          }
        }} 
        size="xl"
        fullscreen="lg-down"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '80vh' }}>
          {/* Custom Navigation Controls */}
          <div style={{ 
            padding: '10px', 
            borderBottom: '1px solid #dee2e6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f8f9fa'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handlePreviousPage}
                disabled={pdfPage <= 1}
              >
                <i className="bi bi-chevron-left"></i> Previous
              </Button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span>Page</span>
                <input
                  type="number"
                  min="1"
                  max={pdfTotalPages}
                  value={pdfPage}
                  onChange={handlePageInput}
                  onBlur={handlePageInputBlur}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur();
                    }
                  }}
                  style={{
                    width: '60px',
                    textAlign: 'center',
                    padding: '2px 5px'
                  }}
                />
                <span>of {pdfTotalPages}</span>
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleNextPage}
                disabled={pdfPage >= pdfTotalPages}
              >
                Next <i className="bi bi-chevron-right"></i>
              </Button>
            </div>
            <div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  if (pdfPreviewUrl) {
                    const link = document.createElement('a');
                    link.href = pdfPreviewUrl;
                    link.setAttribute('download', `report-${selectedJob?.reportId || 'report'}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  }
                }}
              >
                <i className="bi bi-download me-1"></i>
                Download
              </Button>
            </div>
          </div>
          
          {/* PDF Viewer */}
          <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            {pdfPreviewUrl && (
              <iframe
                id="pdf-preview-iframe"
                src={getPdfUrlWithPage(pdfPreviewUrl, pdfPage)}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title="PDF Preview"
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowPdfPreview(false);
              setPdfPage(1);
              setPdfTotalPages(1);
              if (pdfPreviewUrl) {
                window.URL.revokeObjectURL(pdfPreviewUrl);
                setPdfPreviewUrl(null);
              }
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // Get circle name from circles list
  const getCircleName = (circleId) => {
    const circle = circles.find(c => c.id === circleId);
    return circle ? circle.name : circleId;
  };

  // Get grading system name from gradingSystems list
  const getGradingSystemName = (gradingSystemId) => {
    const system = gradingSystems.find(s => s.id === gradingSystemId);
    return system ? system.name : gradingSystemId;
  };

  const escapeCsv = (val) => {
    if (val == null || val === '') return '';
    const s = String(val);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const handleDownloadUsageStats = async () => {
    try {
      setExportingUsageStats(true);
      const [questionnairesRes, reportSummary] = await Promise.all([
        getQuestionnaires(),
        getReportsUsageSummary(),
      ]);

      const qList = Array.isArray(questionnairesRes) ? questionnairesRes : (questionnairesRes?.data ?? questionnairesRes?.result ?? []);
      if (!Array.isArray(qList)) throw new Error('Invalid questionnaires response');

      const byOrg = {};
      for (const q of qList) {
        const oid = String(q.organizationId || q.organization_id || '');
        if (!oid) continue;
        if (!byOrg[oid]) byOrg[oid] = { active: null, draft: null };
        const date = q.updatedAt || q.createdAt || q.updated_at || q.created_at;
        const ts = date ? new Date(date).getTime() : 0;
        const rec = { id: q.questionnaireId || q.id, title: q.title || '', date };

        if ((q.status || '').toUpperCase() === 'ACTIVE') {
          if (!byOrg[oid].active || ts > new Date(byOrg[oid].active.date || 0).getTime()) {
            byOrg[oid].active = rec;
          }
        } else if ((q.status || '').toUpperCase() === 'DRAFT') {
          if (!byOrg[oid].draft || ts > new Date(byOrg[oid].draft.date || 0).getTime()) {
            byOrg[oid].draft = rec;
          }
        }
      }

      const reportByOrg = {};
      for (const r of (Array.isArray(reportSummary) ? reportSummary : [])) {
        reportByOrg[String(r.orgId)] = r;
      }

      const orgIds = [...new Set([...Object.keys(byOrg), ...Object.keys(reportByOrg)])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

      let orgNamesMap = {};
      try {
        orgNamesMap = await getOrganizationNames(orgIds);
      } catch (err) {
        console.warn('Could not fetch organization names:', err);
      }

      const toTitleCase = (s) => s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
      const headers = [
        'organization_id',
        'organization_name',
        'active_questionnaire_id', 'active_questionnaire_title', 'active_questionnaire_date',
        'draft_questionnaire_id', 'draft_questionnaire_title', 'draft_questionnaire_date',
        'last_report_id', 'last_report_name', 'last_report_date', 'last_report_circle_name', 'last_report_grading_system_name',
      ].map(toTitleCase);
      const rows = orgIds.map(oid => {
        const q = byOrg[oid] || {};
        const r = reportByOrg[oid] || {};
        const fmt = (d) => (d ? new Date(d).toISOString() : '');
        const orgName = orgNamesMap[oid] ?? orgNamesMap[String(oid)] ?? '';
        return [
          escapeCsv(oid),
          escapeCsv(orgName),
          escapeCsv(q.active?.id),
          escapeCsv(q.active?.title),
          escapeCsv(fmt(q.active?.date)),
          escapeCsv(q.draft?.id),
          escapeCsv(q.draft?.title),
          escapeCsv(fmt(q.draft?.date)),
          escapeCsv(r.reportId),
          escapeCsv(r.name),
          escapeCsv(r.date),
          escapeCsv(r.circleName ?? r.circleId),
          escapeCsv(r.gradingSystemName ?? r.gradingSystemId),
        ];
      });

      const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `platform-usage-stats-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showNotification('Usage stats exported successfully', 'success');
    } catch (err) {
      console.error('Error exporting usage stats:', err);
      showNotification(err?.message || 'Failed to export usage stats', 'danger');
    } finally {
      setExportingUsageStats(false);
    }
  };

  // Reports Table
  const renderReportsTable = () => (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Reports</h2>
        <div className="d-flex gap-2">
          {hasAllowedRole && (
            <Button
              variant="outline-primary"
              onClick={handleDownloadUsageStats}
              disabled={exportingUsageStats || loading}
            >
              {exportingUsageStats ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Exporting...
                </>
              ) : (
                <>
                  <i className="bi bi-download me-1"></i>
                  Download Usage Stats (CSV)
                </>
              )}
            </Button>
          )}
          <Button 
            variant="outline-secondary" 
            onClick={fetchReports}
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh
          </Button>
          <Button 
            variant="success" 
            onClick={handleOpenCreateModal}
            disabled={!hasActiveQuestionnaire || loading}
          >
            Create Report
          </Button>
        </div>
      </div>
      
      {jobs.length > 0 ? (
        <DataTable
          title="Reports"
          data={jobs}
          collapsible={false}
          columns={[
            {
              key: 'title',
              header: 'Title',
              accessor: (row) => row.title || `Report #${row.reportId}`
            },
            {
              key: 'description',
              header: 'Description',
              accessor: (row) => row.description || row.error || '-'
            },
            {
              key: 'status',
              header: 'Status',
              render: (value, row) => (
                <Badge bg={getStatusVariant(row.status)}>
                  {row.status?.toUpperCase() || 'UNKNOWN'}
                </Badge>
              )
            },
            {
              key: 'circle',
              header: 'Circle',
              accessor: (row) => getCircleName(row.circleId)
            },
            {
              key: 'gradingSystem',
              header: 'Grading System',
              accessor: (row) => getGradingSystemName(row.gradingSystemId)
            },
            {
              key: 'created',
              header: 'Created',
              accessor: (row) => formatDate(row.createdAt)
            },
            {
              key: 'completed',
              header: 'Completed',
              accessor: (row) => formatDate(row.completedAt)
            },
            {
              key: 'actions',
              header: 'Actions',
              render: (value, row) => (
                <div className="d-flex gap-2">
                  <Button
                    size="sm" 
                    variant='success'
                    disabled={row.status !== 'completed'}
                    onClick={() => handleViewReport(row)}
                  >
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant='secondary'
                    onClick={() => handleDeleteJob(row)}
                  >
                    Delete
                  </Button>
                </div>
              )
            }
          ]}
          defaultOpen={true}
          searchable={true}
          pageSize={10}
        />
      ) : (
        <div className="text-center py-4">
          <Alert variant="info">
            No reports found. Click "Create Report" to start.
          </Alert>
        </div>
      )}
    </>
  );

  const renderReportModal = () => (
    <Modal 
      show={showReportModal} 
      onHide={() => setShowReportModal(false)} 
      fullscreen={true}
      backdrop="static"
    >
      <Modal.Header closeButton={false} className="border-bottom shadow-sm bg-white sticky-top">
        <Container fluid>
          <div className="d-flex flex-column">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-1">
                  {selectedJob?.title || 'Report'} - {getCircleName(selectedJob?.circleId)} ({getGradingSystemName(selectedJob?.gradingSystemId)})
                </h4>
                <div className="text-muted small">
                  <span className="me-3">Created: {formatDate(selectedJob?.createdAt)}</span>
                  <span>Completed: {formatDate(selectedJob?.completedAt)}</span>
                </div>
              </div>
              <div className="d-flex gap-2">
                {hasAllowedRole && (
                  <Button 
                    variant="danger"
                    size="sm"
                    disabled={exportingPDF || exportingExcel || !selectedJob || !reportData}
                    onClick={async () => {
                      if (!selectedJob || !reportData) return;
                      try {
                        setExportingPDF(true);
                        const blobUrl = await exportReportToPDF(selectedJob.reportId, {
                          reportData: {
                            ...reportData,
                            circleName: getCircleName(selectedJob?.circleId)
                          },
                          translatedReportData: reportData?.translatedReportData ?? reportData,
                          circleId: selectedJob.circleId,
                          gradingSystemId: selectedJob.gradingSystemId,
                          organizationId: orgId
                        });
                        if (blobUrl) {
                          setPdfPreviewUrl(blobUrl);
                          setPdfPage(1);
                          // Set a reasonable default for total pages
                          // The browser PDF viewer will handle actual navigation
                          // We'll try to detect the actual page count, but default to 18
                          setPdfTotalPages(18);
                          setShowPdfPreview(true);
                          
                          // Try to detect total pages after a short delay
                          setTimeout(() => {
                            try {
                              const iframe = document.getElementById('pdf-preview-iframe');
                              if (iframe && iframe.contentDocument) {
                                // Try to get page count from PDF (may not work due to CORS)
                                // For now, we'll rely on user navigation to determine bounds
                              }
                            } catch (e) {
                              // CORS restrictions - use default
                            }
                          }, 1000);
                        }
                        showNotification('Report exported to PDF successfully', 'success');
                      } catch (err) {
                        console.error('Error exporting to PDF:', err);
                        const errorMsg = err.message || 'Failed to export report to PDF';
                        showNotification(errorMsg, 'danger');
                      } finally {
                        setExportingPDF(false);
                      }
                    }}
                  >
                    <i className="bi bi-file-pdf me-1"></i>
                    {exportingPDF ? 'Exporting...' : 'Export PDF (WIP)'}
                  </Button>
                )}
                <Button 
                  variant="success"
                  size="sm"
                  disabled={exportingPDF || exportingExcel}
                  onClick={async () => {
                    if (!selectedJob || !reportData) return;
                    try {
                      setExportingExcel(true);
                      await exportReportToExcel(selectedJob.reportId, {
                        reportData: {
                          ...reportData,
                          circleName: getCircleName(selectedJob?.circleId)
                        },
                        translatedReportData: reportData?.translatedReportData ?? reportData,
                        circleId: selectedJob.circleId,
                        gradingSystemId: selectedJob.gradingSystemId,
                        organizationId: orgId
                      }, hasAllowedRole, getCircleName(selectedJob?.circleId), getGradingSystemName(selectedJob?.gradingSystemId)); // Pass KAM status and translated names
                      showNotification('Report exported to Excel successfully', 'success');
                    } catch (err) {
                      console.error('Error exporting to Excel:', err);
                      const errorMsg = err.message || 'Failed to export report to Excel';
                      showNotification(errorMsg, 'danger');
                    } finally {
                      setExportingExcel(false);
                    }
                  }}
                >
                  <i className="bi bi-file-excel me-1"></i>
                  {exportingExcel ? 'Exporting...' : 'Export Excel'}
                </Button>
                <Button 
                  style={{
                    backgroundColor: REMCHANNEL_COLORS.steelBlue,
                    borderColor: REMCHANNEL_COLORS.steelBlue,
                    color: '#fff'
                  }}
                  size="sm"
                  onClick={() => setShowReportModal(false)}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </Modal.Header>
      <Modal.Body className="p-0 bg-light">
        <Container fluid className="py-4">
          <div className="bg-white rounded shadow-sm p-4">
            <ReportSections 
              reportData={reportData} 
              reportId={selectedJob?.reportId}
              circleId={selectedJob?.circleId}
              circleName={getCircleName(selectedJob?.circleId)}
              gradingSystemId={selectedJob?.gradingSystemId}
              gradingSystemName={selectedJob?.gradingSystemName || getGradingSystemName(selectedJob?.gradingSystemId)}
              organizationId={orgId}
              onReportRefresh={async () => {
                // Reload the report data after retranslation
                if (selectedJob?.reportId) {
                  try {
                    const resultResponse = await getReportById(selectedJob.reportId);
                    if (resultResponse?.reportData) {
                      setReportData({
                        ...resultResponse.reportData,
                        isTranslated: resultResponse.isTranslated ?? !!resultResponse.translatedReportData
                      });
                      showNotification('Report grades retranslated successfully', 'success');
                    }
                  } catch (error) {
                    console.error('Error refreshing report data:', error);
                    showNotification('Failed to refresh report data', 'danger');
                  }
                }
              }}
            />
          </div>
        </Container>
      </Modal.Body>
    </Modal>
  );

  const handleDeleteJob = async (job) => {
    if (!window.confirm(`Are you sure you want to delete report "${job.title || job.reportId}"? This action cannot be undone.`)) {
      return;
    }
    try {
      setLoading(true);
      await deleteReport(job.reportId);
      showNotification('Report deleted successfully', 'success');
      // Refresh jobs list
      const reportsResponse = await getReportsByOrganization(orgId);
      if (reportsResponse) {
        setJobs(reportsResponse.map(report => ({
          reportId: report.id,
          title: report.name,
          description: report.description,
          status: report.status,
          circleId: report.circleId,
          circleName: report.circleName,
          gradingSystemId: report.gradingSystemId,
          gradingSystemName: report.gradingSystemName,
          createdAt: report.createdAt,
          completedAt: report.completedAt,
          error: report.error
          // The list omits reportData and translatedReportData; open the report by id for the full payload.
        })));
      }
    } catch (err) {
      showNotification('Failed to delete report', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      await checkActiveQuestionnaire();
      const response = await getReportsByOrganization(orgId);
      
      if (response) {
        // Handle both array and object with result property
        const reports = Array.isArray(response) ? response : response.result || [];
        setJobs(reports.map(report => ({
          reportId: report.id,
          title: report.name,
          description: report.description,
          status: report.status,
          circleId: report.circleId,
          circleName: circles.find(c => c.id === report.circleId)?.name || report.circleId,
          gradingSystemId: report.gradingSystemId,
          gradingSystemName: gradingSystems.find(s => s.id === report.gradingSystemId)?.name || report.gradingSystemId,
          createdAt: report.createdAt,
          completedAt: report.completedAt,
          error: report.error
          // The list omits reportData and translatedReportData; open the report by id for the full payload.
        })));
      } else {
        setJobs([]);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      setError('Failed to fetch reports. Please try again.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const checkActiveQuestionnaire = async () => {
    try {
      const response = await getQuestionnairesByOrganization(orgId);
      // Check if response exists and has result property
      if (response && Array.isArray(response)) {
        setHasActiveQuestionnaire(response.some(q => q.status === 'ACTIVE'));
      } else if (response && response.result && Array.isArray(response.result)) {
        setHasActiveQuestionnaire(response.result.some(q => q.status === 'ACTIVE'));
      } else {
        setHasActiveQuestionnaire(false);
      }
    } catch (error) {
      console.error('Error checking active questionnaire:', error);
      setError('Failed to check active questionnaire status.');
      setHasActiveQuestionnaire(false);
    }
  };

  if (loading && circles.length === 0 && gradingSystems.length === 0 && jobs.length === 0) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="success" />
        <p className="mt-3">Loading reporting data...</p>
      </div>
    );
  }

  return (
    <Container>
      {renderReportsTable()}
      {renderCreateReportModal()}
      {renderReportModal()}
      {renderPdfPreviewModal()}
      {/* Toast notifications */}
      <ToastMessage
        show={showToast}
        variant={toastVariant}
        message={toastMessage}
        onClose={hideNotification}
      />
    </Container>
  );
};

export default Reporting; 